import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { ProjectUnlockData } from '@/types/project';
import { formatCompactNumber } from '@/utils';

interface UnlockPieChartProps {
  unlockData: ProjectUnlockData;
}

interface TooltipPayloadItem {
  payload: {
    name: string;
    value: number;
    percentage: string;
  };
}

const UnlockPieChart: React.FC<UnlockPieChartProps> = ({ unlockData }) => {
  // Prepare data for pie chart - Figma colors
  const pieData = [
    {
      name: 'Unlocked',
      value: unlockData.total_unlocked,
      percentage: ((unlockData.total_unlocked / unlockData.total_supply) * 100).toFixed(1),
      color: '#c4e804' // Figma green-lime
    },
    {
      name: 'Locked',
      value: unlockData.total_locked,
      percentage: ((unlockData.total_locked / unlockData.total_supply) * 100).toFixed(1),
      color: '#6b6969' // Figma gray
    }
  ];

  // Filter out segments with zero values
  const filteredPieData = pieData.filter(item => item.value > 0);

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: TooltipPayloadItem[] }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white dark:bg-[#1a1a1a] p-3 border border-[rgba(45,41,35,0.12)] dark:border-[rgba(253,253,253,0.12)] shadow-lg">
          <p className="font-['Plus_Jakarta_Sans'] font-medium text-[#2d2923] dark:text-[#fdfdfd]">{data.name}</p>
          <p className="font-['Plus_Jakarta_Sans'] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            {formatCompactNumber(data.value)} tokens
          </p>
          <p className="font-['Plus_Jakarta_Sans'] text-[13px] text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)]">
            {data.percentage}% of supply
          </p>
        </div>
      );
    }
    return null;
  };

  // Calculate positions for percentage badges
  const unlockedPercent = Math.round((unlockData.total_unlocked / unlockData.total_supply) * 100);
  const lockedPercent = 100 - unlockedPercent;

  return (
    <div className="relative w-full h-full">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={filteredPieData}
            cx="50%"
            cy="50%"
            innerRadius={70}
            outerRadius={120}
            paddingAngle={0}
            dataKey="value"
            strokeWidth={0}
          >
            {filteredPieData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
        </PieChart>
      </ResponsiveContainer>
      
      {/* Percentage badges positioned around the chart */}
      <div 
        className="absolute bg-white border border-[#e0e0e0] rounded-full px-3 py-2 shadow-lg flex items-center justify-center"
        style={{ left: '0', top: '60%', transform: 'translateY(-50%)' }}
      >
        <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] text-[#2d2923]">
          {unlockedPercent}%
        </span>
      </div>
      <div 
        className="absolute bg-white border border-[#e0e0e0] rounded-full px-3 py-2 shadow-lg flex items-center justify-center"
        style={{ right: '0', top: '30%', transform: 'translateY(-50%)' }}
      >
        <span className="font-['Plus_Jakarta_Sans'] font-medium text-[13px] text-[#2d2923]">
          {lockedPercent}%
        </span>
      </div>
    </div>
  );
};

export default UnlockPieChart;
